import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { SupabaseClient } from '../../lib/supabase';
import type { RecordPreview } from './linkPreview';
import { fetchRecordPreview, opensInPlace } from './linkPreview';
import type { ParsedHubLink } from './linkPreview';

/**
 * The live preview card for a pasted Hub link (plan-drop3-extras.md P1-P5).
 *
 * Cached per record while the page stays open (P4) — a module-level Map
 * rather than React Query, because the cache has to survive a message being
 * scrolled out of view and back without re-issuing the read, and it must
 * never persist past a page reload: a stale deal stage shown days later would
 * be worse than asking again.
 */
const previewCache = new Map<string, RecordPreview>();

function cacheKey(link: ParsedHubLink): string {
  return `${link.kind}:${link.id}`;
}

/** Exposed for tests: clears the module-level cache between cases. */
export function clearPreviewCache(): void {
  previewCache.clear();
}

function formatDate(value: string | null): string {
  if (value === null) {
    return 'none';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'none';
  }
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function LinkPreviewCard({
  client,
  link,
  href,
  onOpenLink,
}: {
  client: SupabaseClient;
  link: ParsedHubLink;
  /** The exact href from the message, so a refused read still links out. */
  href: string;
  /** Messages opens a task card in place (P1); true means it did. */
  onOpenLink?: (href: string) => boolean;
}) {
  const key = cacheKey(link);
  const [preview, setPreview] = useState<RecordPreview | undefined>(previewCache.get(key));

  useEffect(() => {
    if (previewCache.has(key)) {
      setPreview(previewCache.get(key));
      return;
    }
    let cancelled = false;
    void fetchRecordPreview(client, link).then((result) => {
      if (cancelled) return;
      previewCache.set(key, result);
      setPreview(result);
    });
    return () => {
      cancelled = true;
    };
  }, [client, key, link]);

  // Still loading, or RLS refused the row: a plain link either way (P2). No
  // "no access" text — that would confirm the record exists.
  if (preview === undefined || preview === null) {
    return (
      <a className="link-preview-plain" href={href} target="_blank" rel="noreferrer noopener">
        {href}
      </a>
    );
  }

  if (preview.kind === 'deal') {
    return (
      <Link className="link-preview-card" to={`/deals?deal=${link.id}`}>
        <span className="link-preview-kind link-preview-kind--deal">Deal</span>
        <span className="link-preview-title">{preview.name}</span>
        <span className="link-preview-meta">
          {preview.stage ?? 'No stage'} · next action {formatDate(preview.nextActionDate)} ·{' '}
          {preview.owner ?? 'Unassigned'}
        </span>
      </Link>
    );
  }

  if (preview.kind === 'lead') {
    return (
      <Link className="link-preview-card" to={`/tracker?lead=${link.id}`}>
        <span className="link-preview-kind link-preview-kind--lead">Lead</span>
        <span className="link-preview-title">{preview.name}</span>
        <span className="link-preview-meta">
          {preview.company} · {preview.stage ?? 'No stage'}
        </span>
      </Link>
    );
  }

  if (preview.kind === 'order') {
    return (
      <Link className="link-preview-card" to={`/cg-orders/${link.id}`}>
        <span className="link-preview-kind link-preview-kind--order">Order</span>
        <span className="link-preview-title">{preview.orderNo}</span>
        <span className="link-preview-meta">
          {preview.client} · {preview.status} · {preview.owner ?? 'Unassigned'}
        </span>
      </Link>
    );
  }

  const taskHref = `/projects/${preview.projectId}?task=${link.id}`;
  return (
    <Link
      className="link-preview-card"
      to={taskHref}
      onClick={(event) => {
        if (onOpenLink !== undefined && opensInPlace(event) && onOpenLink(taskHref)) {
          event.preventDefault();
        }
      }}
    >
      <span className="link-preview-kind link-preview-kind--task">Task</span>
      <span className="link-preview-title">{preview.title}</span>
      <span className="link-preview-meta">
        {preview.status ?? 'No status'} · {preview.assignee ?? 'Unassigned'} · due{' '}
        {formatDate(preview.dueDate)}
      </span>
    </Link>
  );
}
